import { RoundedBoxGeometry } from '@react-three/drei';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { readableColor } from '@slope/uikit';
import * as THREE from 'three';
import { toVec3 } from '@slope/three';
import { useFrame, useThree } from '@react-three/fiber';
import { FloatingPanel, FloatingPanelProps } from '@slope/r3f';
import { TeeIcon, GreenIcon } from '@slope/icons';
import { useSignVisibility } from '../../hooks';

const ObstacleSignContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['bg'].includes(prop),
  displayName: 'ObstacleSignRow',
})<{ bg?: React.CSSProperties['backgroundColor'] }>`
  border: 0.06px solid black;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-size: 1.2rem;
  font-family: 'Barlow', sans-serif;
  font-weight: 400;
  font-style: normal;
  padding: 0.25em;
  color: ${({ bg = 'white' }) => readableColor(bg, 'black', 'white')};
  background-color: ${({ bg = 'white' }) => bg};
`;

const ObstacleSignRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 1.3em;
  transition:
    background-color,
    color 0.3s ease-in-out;
  gap: 0.25em;
  & > svg {
    flex-shrink: 0;
    width: 1em;
    height: 1em;
  }
`;

const ObstacleSignLabel = styled.div`
  text-align: right;
  width: 2em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ObstacleSignDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 1);
  margin: 0.25em 0;
`;

export type ObstacleSignProps = FloatingPanelProps & {
  /**
   * The position of the tee.
   */
  teePos: THREE.Vector3;
  /**
   * The position of the green.
   */
  greenPos: THREE.Vector3;
  /**
   * The color of the Obstacle sign.
   */
  color?: string;
  /**
   * The size of the Obstacle sign.
   */
  size?: number;
};

export const ObstacleSign: React.FC<ObstacleSignProps> = ({
  color,
  teePos,
  greenPos,
  position,
  ...restProps
}) => {
  const camera = useThree((state) => state.camera);
  const positionVec = useMemo(() => toVec3(position ?? []), [position]);
  const teeDistance = useMemo(
    () => (position ? teePos.distanceTo(positionVec) : 0),
    [teePos, positionVec],
  );
  const greenDistance = useMemo(
    () => (greenPos ? greenPos.distanceTo(positionVec) : 0),
    [greenPos, positionVec],
  );

  const isVisible = useSignVisibility(positionVec, camera, 100);

  const isGreenSignVisible = greenDistance > 50;
  const isTeeSignVisible = teeDistance > 50;

  return (
    <FloatingPanel
      transform
      sprite
      distanceFactor={50}
      lineWidth={1}
      dotSize={3}
      //occlude="blending"
      position={position}
      offset={[0, -50]}
      margin={10}
      style={{ opacity: isVisible ? '1' : '0', transition: 'opacity 0.3s ease-in-out' }}
      {...restProps}
      geometry={<RoundedBoxGeometry args={[1, 0.5, 0.1]} radius={0.05} smoothness={4} />}
      zIndexRange={[20, 200]}
      pointerEvents="none"
    >
      <ObstacleSignContainer bg={color}>
        {isGreenSignVisible && (
          <ObstacleSignRow>
            <ObstacleSignLabel>{greenDistance.toFixed(0)}</ObstacleSignLabel>
            <GreenIcon />
          </ObstacleSignRow>
        )}
        {isGreenSignVisible && isTeeSignVisible && <ObstacleSignDivider />}
        {isTeeSignVisible && (
          <ObstacleSignRow>
            <ObstacleSignLabel>{teeDistance.toFixed(0)}</ObstacleSignLabel>
            <TeeIcon />
          </ObstacleSignRow>
        )}
      </ObstacleSignContainer>
    </FloatingPanel>
  );
};
